import { AlertCircle, FileText, Loader2, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import Button from '../../../components/Button';
import InlineErrorDisplay from '../../../components/InlineErrorDisplay';
import { exportImportService } from '../services/exportImportService';
import type { ExportData, ExportedLesson, ExportedSection, ImportOptions, ImportResult, LearningSection } from '../services/learningTypes';

interface ImportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onImportComplete: () => void;
    sections: LearningSection[];
}

export default function ImportDialog({
    isOpen,
    onClose,
    onImportComplete,
    sections,
}: ImportDialogProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<ExportData | null>(null);
    const [importOptions, setImportOptions] = useState<ImportOptions>({
        overwriteExisting: false,
        preserveIds: true,
        targetSectionId: undefined,
    });
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setError(null);
            setSelectedFile(file);
            setPreviewData(null);
            setImportResult(null);

            const exportData = await exportImportService.parseImportFile(file);
            setPreviewData(exportData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to parse file');
            setSelectedFile(null);
        }
    };

    const handleImport = async () => {
        if (!previewData) return;

        try {
            setLoading(true);
            setError(null);
            setImportResult(null);

            const result = await exportImportService.importData(previewData, importOptions);
            setImportResult(result);

            if (result.success) {
                onImportComplete();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to import data');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setPreviewData(null);
        setImportResult(null);
        setError(null);
        setImportOptions({
            overwriteExisting: false,
            preserveIds: true,
            targetSectionId: undefined,
        });
        onClose();
    };

    const needsTargetSection = previewData?.type === 'lessons';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                    <div className="flex items-center gap-3">
                        <Upload className="w-5 h-5 text-blue-600" />
                        <h2 className="text-xl font-semibold">Import Learning Content</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
                    {/* File Upload */}
                    {!selectedFile && (
                        <div className="space-y-4">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-8 text-center">
                                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    Select Export File
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    Choose a JSON file that was exported from db-admin
                                </p>
                                <Button onClick={() => fileInputRef.current?.click()}>
                                    Choose File
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-yellow-900 mb-1">Important Notes</h4>
                                        <ul className="text-sm text-yellow-800 space-y-1">
                                            <li>• Only import files that were exported from db-admin</li>
                                            <li>• Importing will create new content in your database</li>
                                            <li>• Consider backing up your data before importing large datasets</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* File Selected - Show Preview and Options */}
                    {selectedFile && previewData && !importResult && (
                        <div className="space-y-6">
                            {/* File Info */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h3 className="font-medium text-green-900 mb-2">File Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                    <div className="min-w-0 break-words">
                                        <span className="text-green-700 font-medium">File:</span>
                                        <span className="text-green-600 ml-2">{selectedFile.name}</span>
                                    </div>
                                    <div>
                                        <span className="text-green-700 font-medium">Type:</span>
                                        <span className="text-green-600 ml-2 capitalize">{previewData.type}</span>
                                    </div>
                                    <div>
                                        <span className="text-green-700 font-medium">Version:</span>
                                        <span className="text-green-600 ml-2">{previewData.version}</span>
                                    </div>
                                    <div>
                                        <span className="text-green-700 font-medium">Export Date:</span>
                                        <span className="text-green-600 ml-2">
                                            {new Date(previewData.exportDate).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Content Preview */}
                            <div className="border rounded-lg p-4">
                                <h3 className="font-medium mb-3">Content Preview</h3>
                                {previewData.type === 'section' && (
                                    <div className="space-y-2">
                                        <p className="text-sm">
                                            <strong>Section:</strong> {(previewData.data as ExportedSection).section.title}
                                        </p>
                                        <p className="text-sm">
                                            <strong>Lessons:</strong> {(previewData.data as ExportedSection).lessons.length}
                                        </p>
                                        <p className="text-sm">
                                            <strong>Total Pages:</strong> {(previewData.data as ExportedSection).lessons.reduce((sum: number, lesson: ExportedLesson) => sum + lesson.pages.length, 0)}
                                        </p>
                                    </div>
                                )}
                                {previewData.type === 'lessons' && (
                                    <div className="space-y-2">
                                        <p className="text-sm">
                                            <strong>Lessons:</strong> {(previewData.data as ExportedLesson[]).length}
                                        </p>
                                        <p className="text-sm">
                                            <strong>Total Pages:</strong> {(previewData.data as ExportedLesson[]).reduce((sum: number, lesson: ExportedLesson) => sum + lesson.pages.length, 0)}
                                        </p>
                                        <div className="text-sm mt-2">
                                            <strong>Lesson Titles:</strong>
                                            <ul className="list-disc list-inside ml-4 mt-1">
                                                {(previewData.data as ExportedLesson[]).slice(0, 5).map((lesson: ExportedLesson, index: number) => (
                                                    <li key={index}>{lesson.lesson.title}</li>
                                                ))}
                                                {(previewData.data as ExportedLesson[]).length > 5 && (
                                                    <li>... and {(previewData.data as ExportedLesson[]).length - 5} more</li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Import Options */}
                            <div className="border rounded-lg p-4 space-y-4">
                                <h3 className="font-medium">Import Options</h3>

                                <div className="space-y-3">
                                    <label className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={importOptions.preserveIds}
                                            onChange={(e) => setImportOptions(prev => ({ ...prev, preserveIds: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <div>
                                            <span className="font-medium">Preserve Original IDs</span>
                                            <p className="text-sm text-gray-600">Use the same IDs from the export file</p>
                                        </div>
                                    </label>

                                    <label className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={importOptions.overwriteExisting}
                                            onChange={(e) => setImportOptions(prev => ({ ...prev, overwriteExisting: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <div>
                                            <span className="font-medium">Overwrite Existing Content</span>
                                            <p className="text-sm text-gray-600">Update existing content with the same ID</p>
                                        </div>
                                    </label>

                                    {needsTargetSection && (
                                        <div>
                                            <label className="block font-medium mb-2">Target Section *</label>
                                            <select
                                                value={importOptions.targetSectionId || ''}
                                                onChange={(e) => setImportOptions(prev => ({ ...prev, targetSectionId: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="">Select a section</option>
                                                {sections.map((section) => (
                                                    <option key={section.uid} value={section.uid}>
                                                        {section.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={() => setSelectedFile(null)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                                ← Choose a different file
                            </button>
                        </div>
                    )}

                    {/* Import Result */}
                    {importResult && (
                        <div className="space-y-4">
                            <div className={`border rounded-lg p-4 ${importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <h3 className={`font-medium mb-2 ${importResult.success ? 'text-green-900' : 'text-red-900'}`}>
                                    Import {importResult.success ? 'Successful' : 'Failed'}
                                </h3>
                                <p className={`text-sm ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                    {importResult.message}
                                </p>
                            </div>

                            {importResult.success && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-medium text-blue-900 mb-2">Import Summary</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <span className="text-blue-700 font-medium">Sections:</span>
                                            <span className="text-blue-600 ml-2">{importResult.importedIds.sectionIds.length}</span>
                                        </div>
                                        <div>
                                            <span className="text-blue-700 font-medium">Lessons:</span>
                                            <span className="text-blue-600 ml-2">{importResult.importedIds.lessonIds.length}</span>
                                        </div>
                                        <div>
                                            <span className="text-blue-700 font-medium">Pages:</span>
                                            <span className="text-blue-600 ml-2">{importResult.importedIds.pageIds.length}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {importResult.errors.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <h4 className="font-medium text-yellow-900 mb-2">Warnings</h4>
                                    <ul className="text-sm text-yellow-700 space-y-1">
                                        {importResult.errors.map((err, index) => (
                                            <li key={index} className="break-words">• {err}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <Button onClick={handleClose} className="w-full">
                                Close
                            </Button>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4">
                            <InlineErrorDisplay message={error} />
                        </div>
                    )}
                </div>

                {/* Footer Actions - Only show if file is selected but no result yet */}
                {selectedFile && previewData && !importResult && (
                    <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t bg-gray-50">
                        <Button variant="outline" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleImport}
                            disabled={loading || (needsTargetSection && !importOptions.targetSectionId)}
                            className="min-w-[120px]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Import
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
